import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface Customer {
  id: string;
  name: string;
  last_name: string;
  customer_type: string;
  business_name?: string | null;
  representative_first_name?: string | null;
  representative_last_name?: string | null;
  cell_numbers: string[];
  emails: string[];
  city?: string;
  province_state?: string;
  suburb?: string;
  postal_code?: string;
  street_address?: string;
  lead_source?: string;
  lead_source_description?: string;
  lead_owner_id?: string;
  referred_by_id?: string;
  tags?: string[];
  created_at: string;
}

export interface Profile {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
}

export interface Project {
  id: string;
  name: string;
  description?: string | null;
  expected_start_date?: string | null;
  expected_end_date?: string | null;
  actual_start_date?: string | null;
  actual_end_date?: string | null;
  status?: string;
  customer_id?: string;
  created_by: string;
  company_id: string;
  use_separate_site_address?: boolean;
  site_street_address?: string | null;
  site_suburb?: string | null;
  site_city?: string | null;
  site_province_state?: string | null;
  site_postal_code?: string | null;
  created_at: string;
  updated_at: string;
  customers?: Customer | null;
  profiles?: Profile | null;
  // Calculated fields
  project_duration_days?: number | null;
  is_overdue?: boolean;
  has_customer?: boolean;
  has_site_address?: boolean;
}

export const useProjectData = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchProjects = useCallback(async () => {
    try {
      // Fetch projects with basic joins first to avoid foreign key issues
      const { data: projectsData, error } = await supabase
        .from('projects')
        .select(`
          *,
          customers (
            id,
            name,
            last_name,
            customer_type,
            business_name,
            representative_first_name,
            representative_last_name,
            cell_numbers,
            emails,
            city,
            province_state,
            suburb,
            postal_code,
            street_address,
            lead_source,
            lead_source_description,
            lead_owner_id,
            referred_by_id,
            tags,
            created_at
          )
        `)
        .order(sortBy, { ascending: sortDir === 'asc' });

      if (error) {
        console.error('Error fetching projects:', error);
        return;
      }

      const projectsWithCalculations: Project[] = [];

      // Early exit if no projects
      if (!projectsData || projectsData.length === 0) {
        setProjects([]);
        return;
      }

      // Build final results with calculations
      for (const project of projectsData || []) {
        // Calculate project duration
        let projectDurationDays = null;
        if (project.expected_start_date && project.expected_end_date) {
          const startDate = new Date(project.expected_start_date);
          const endDate = new Date(project.expected_end_date);
          projectDurationDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        }

        // Check if project is overdue
        const isOverdue = project.expected_end_date && !project.actual_end_date 
          ? new Date(project.expected_end_date) < new Date()
          : false;

        // Handle potential join errors - use optional chaining and type guards
        const safeProject: Project = {
          ...project,
          customers: project.customers && 'id' in project.customers ? project.customers as Customer : null,
          profiles: null // No profiles data since we removed the join
        };

        projectsWithCalculations.push({
          ...safeProject,
          project_duration_days: projectDurationDays,
          is_overdue: isOverdue,
          has_customer: !!project.customer_id,
          has_site_address: !!project.use_separate_site_address,
        });
      }

      setProjects(projectsWithCalculations);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, [sortBy, sortDir]);

  // Apply search to projects
  const searchedProjects = useMemo(() => {
    if (!search.trim()) return projects;

    const searchTerm = search.toLowerCase();
    return projects.filter(project => {
      const customerName = project.customers 
        ? `${project.customers.name} ${project.customers.last_name}`.toLowerCase()
        : '';

      return (
        project.name?.toLowerCase().includes(searchTerm) ||
        project.description?.toLowerCase().includes(searchTerm) ||
        customerName.includes(searchTerm) ||
        project.status?.toLowerCase().includes(searchTerm) ||
        project.site_street_address?.toLowerCase().includes(searchTerm) ||
        project.site_city?.toLowerCase().includes(searchTerm)
      );
    });
  }, [projects, search]);

  // Initial fetch and re-fetch when sort parameters change
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects: searchedProjects,
    fetchProjects,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  };
};